// In-memory transfer store (F11.2 / F14.3).
//
// Holds the live Transfer map keyed by id. Subscribers (React via
// `useTransfers`) receive a snapshot on every mutation. We deliberately
// roll our own rather than pull zustand — the surface here is small and
// the consumers (FileCard, Bubble, MeshProvider) only need three operations.
//
// Speed tracking:
//   - On every `update()` whose `bytesSent` rises, we compute a 3-sample
//     rolling mean of (delta-bytes / delta-time) and write it back as
//     `speedMBps`.
//   - The tick cadence is owned by the caller (MeshProvider's send loop).
//     The store stays cadence-agnostic so a faster or slower upstream
//     doesn't break the smoothing window.
//
// Why useSyncExternalStore: React's official subscription primitive avoids
// the tearing/strict-mode quirks of a hand-rolled context+forceUpdate
// pattern, and `getSnapshot` is naturally stable across re-renders as
// long as we hand back the same `Transfer[]` array reference when no
// state has changed.

import { useRef, useSyncExternalStore } from 'react';

const SPEED_SAMPLE_WINDOW = 3;
const MB = 1024 * 1024;

// Always a valid RFC-4122 UUID. A transfer id can end up packed into the
// binary file-chunk frame (envelope.js#uuidToBytes), which rejects non-UUIDs —
// and crypto.randomUUID is undefined in insecure contexts (a phone on a plain
// http LAN IP). Build a v4 from getRandomValues so the fallback is still valid.
function makeUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const b = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(b);
  } else {
    for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  }
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h
    .slice(6, 8)
    .join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`;
}

function now() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

function createStore() {
  /** @type {Map<string, Transfer>} */
  const transfers = new Map();
  /** @type {Map<string, Array<{ bytes: number, ms: number }>>} */
  const speedSamples = new Map();
  /** @type {Set<() => void>} */
  const listeners = new Set();

  // Cached array snapshot. We invalidate it on every mutation so
  // `getSnapshot` returns a referentially-stable list during quiet periods
  // (lets useSyncExternalStore bail out of re-renders).
  let cachedList = null;

  const invalidate = () => {
    cachedList = null;
  };
  const emit = () => {
    invalidate();
    for (const fn of listeners) {
      try {
        fn();
      } catch {
        /* one bad subscriber shouldn't take the others down */
      }
    }
  };

  /**
   * Create a new Transfer record. Caller passes the immutable fields;
   * we initialise the running counters to zero and the timestamps to now.
   *
   * @param {{
   *   id?: string,
   *   kind: 'file' | 'code',
   *   via: 'webrtc',
   *   name: string,
   *   size: number,
   *   mime: string,
   *   fromId: string,
   *   toId: string,
   * }} init
   * @returns {Transfer}
   */
  const create = (init) => {
    if (!init || typeof init !== 'object') {
      throw new TypeError('transferStore.create: init object required');
    }
    const id = init.id || makeUuid();
    const t = {
      id,
      kind: init.kind,
      via: init.via,
      name: init.name,
      size: Number(init.size) || 0,
      mime: init.mime,
      fromId: init.fromId,
      toId: init.toId,
      progress: 0,
      state: 'pending',
      bytesSent: 0,
      startedAt: now(),
      lastTickAt: now(),
      lastTickBytes: 0,
      speedMBps: 0,
    };
    transfers.set(id, t);
    speedSamples.set(id, []);
    emit();
    return t;
  };

  const update = (id, patch) => {
    const t = transfers.get(id);
    if (!t || !patch) return;

    let nextBytes = t.bytesSent;
    if (typeof patch.bytesSent === 'number' && patch.bytesSent > t.bytesSent) {
      nextBytes = patch.bytesSent;
    }

    // Sliding 3-sample mean speed in MB/s. Recompute only when bytes moved
    // forward — passive state-only patches (pause/resume/done) shouldn't
    // skew the average.
    let nextSpeed = t.speedMBps;
    let nextLastTickAt = t.lastTickAt;
    let nextLastTickBytes = t.lastTickBytes;
    if (nextBytes > t.bytesSent) {
      const tNow = now();
      const deltaBytes = nextBytes - t.lastTickBytes;
      const deltaMs = Math.max(1, tNow - t.lastTickAt);
      const samples = speedSamples.get(id) || [];
      samples.push({ bytes: deltaBytes, ms: deltaMs });
      while (samples.length > SPEED_SAMPLE_WINDOW) samples.shift();
      speedSamples.set(id, samples);

      const totalBytes = samples.reduce((s, x) => s + x.bytes, 0);
      const totalMs = samples.reduce((s, x) => s + x.ms, 0);
      // bytes/ms → MB/s : (b / ms) * 1000 / (1024*1024)
      nextSpeed = totalMs > 0 ? (totalBytes * 1000) / totalMs / MB : 0;
      nextLastTickAt = tNow;
      nextLastTickBytes = nextBytes;
    }

    // Progress: prefer caller's value when supplied, else derive from bytes.
    let nextProgress = t.progress;
    if (typeof patch.progress === 'number') {
      nextProgress = clampPct(patch.progress);
    } else if (nextBytes !== t.bytesSent && t.size > 0) {
      nextProgress = clampPct((nextBytes / t.size) * 100);
    }
    // `done` always pins progress to 100 — receivers can land done with
    // bytesSent unchanged (cloud path).
    if (patch.state === 'done') nextProgress = 100;

    const next = {
      ...t,
      ...patch,
      bytesSent: nextBytes,
      progress: nextProgress,
      lastTickAt: nextLastTickAt,
      lastTickBytes: nextLastTickBytes,
      speedMBps: nextSpeed,
    };
    transfers.set(id, next);
    emit();
  };

  const remove = (id) => {
    if (!transfers.has(id)) return;
    transfers.delete(id);
    speedSamples.delete(id);
    emit();
  };

  const list = (filter) => {
    if (cachedList === null) {
      cachedList = Array.from(transfers.values());
    }
    if (typeof filter === 'function') {
      return cachedList.filter(filter);
    }
    return cachedList;
  };

  const subscribe = (listener) => {
    if (typeof listener !== 'function') return () => {};
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  // Test-only resets — used by store unit tests so the singleton doesn't
  // leak between cases. Not exposed on the public surface.
  const _reset = () => {
    transfers.clear();
    speedSamples.clear();
    listeners.clear();
    cachedList = null;
  };

  return { create, update, remove, list, subscribe, _reset };
}

function clampPct(v) {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 100) return 100;
  return v;
}

/**
 * Singleton store. Importers share state across the app — that's the whole
 * point: MeshProvider writes, Bubble/FileCard read.
 */
export const transferStore = createStore();

/**
 * React subscription hook. Re-renders whenever the store mutates.
 *
 * Implementation: useSyncExternalStore demands that getSnapshot return the
 * SAME reference between calls when nothing has changed — otherwise React
 * detects "state changed" and re-runs in an infinite loop. We achieve
 * this by:
 *   1. The store invalidates its `cachedList` on every mutation only.
 *   2. The hook memoises a (filter, lastList) → filteredList pair so that
 *      back-to-back getSnapshot() calls on the same underlying list
 *      return the same filtered array reference.
 */
export function useTransfers(filter) {
  // Per-render closure over a cache cell. We rebuild the snapshot only
  // when the underlying list reference changes; otherwise we return the
  // memoised filtered array. This is `useSyncExternalStore`'s contract.
  const cache = useTransfersCacheRef();
  const subscribe = (cb) => transferStore.subscribe(cb);
  const getSnapshot = () => {
    const full = transferStore.list();
    if (cache.lastFull === full && cache.lastFilter === filter) {
      return cache.lastResult;
    }
    cache.lastFull = full;
    cache.lastFilter = filter;
    cache.lastResult = typeof filter === 'function' ? full.filter(filter) : full;
    return cache.lastResult;
  };
  const getServerSnapshot = () => EMPTY_LIST;
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

// Tiny module-level helper — gives the hook a per-component cache.
const EMPTY_LIST = Object.freeze([]);
function useTransfersCacheRef() {
  const ref = useRef(null);
  if (ref.current === null) {
    ref.current = { lastFull: null, lastFilter: null, lastResult: EMPTY_LIST };
  }
  return ref.current;
}

export const __test__ = { SPEED_SAMPLE_WINDOW, createStore };
